export enum ProductTypeEnum {
  ETF = 'ETF',
  FONDI = 'FONDI',
  BOND = 'BOND',
}

// =============================================================================
// OPTIMIZATION-ONLY CONSTRAINT TYPES (sent to backend)
// =============================================================================

/**
 * Main constraint structure for products
 * Contains only optimization-relevant data - display data fetched separately
 */
export interface ProductConstraints {
  /**
   * Individual products added via search bar
   * Key: ISIN (e.g., "IE00B4L5Y983")
   */
  individual_products: Record<string, ProductItemConstraint>;

  /**
   * Products from selected lists
   * Key: ISIN
   * List membership determined dynamically via /product-lists endpoint
   */
  list_products: Record<string, ProductItemConstraint>;

  /**
   * Selected list IDs (UI state - tracks which checkboxes are checked)
   */
  selected_list_ids: string[];

  /**
   * Portfolio products from uploaded/existing portfolio (rebalancing mode only)
   * Key: ISIN
   */
  portfolio_products: Record<string, PortfolioProductConstraint> | null;

  /**
   * When true, only list_products are used for optimization
   * When false, both list_products and individual_products are used
   * Default: true
   */
  only_list_products: boolean;
}

/**
 * Constraint for an individual or list product
 * Optimization-only - no display fields
 */
export interface ProductItemConstraint {
  weight_min: number | null; // 0-1 scale (0.05 = 5%)
  weight_max: number | null; // 0-1 scale (0.50 = 50%)
  value_min: number | null; // EUR amount (rebalancing only)
  value_max: number | null; // EUR amount (rebalancing only)
}

/**
 * Hold mode for portfolio products
 * - 'none': No hold constraint, weight/value constraints can be changed
 * - 'hold': Hold position as-is, optimizer keeps allocation unchanged
 * - 'hold_or_sell': Hold or sell if necessary, optimizer can sell but won't buy more
 */
export type HoldMode = 'none' | 'hold' | 'hold_or_sell';

/**
 * Constraint for a portfolio product (rebalancing mode)
 * Optimization-only - no display or current/target fields
 */
export interface PortfolioProductConstraint {
  weight_min: number | null;
  weight_max: number | null;
  value_min: number | null;
  value_max: number | null;
  hold_mode: HoldMode; // Controls how optimizer handles this position
  investment_type?: string | null; // Fineco position type (SIC, BS, OBB, PAC)
  allowed_hold_modes?: HoldMode[]; // Hold modes permitted by investment type (read-only, derived)
  held_currencies?: string[]; // Currencies of the held listings (read-only, derived)
  held_markets?: string[]; // Markets of the held listings (read-only, derived)
}

// =============================================================================
// DISPLAY DATA TYPES (fetched on-demand via API)
// =============================================================================

/**
 * Consultability status for a product
 * - 'consultable': Lock can be changed freely (all hold modes available)
 * - 'maintainable': Only hold_or_sell or hold modes allowed (no 'none')
 * - 'non_consultable': Only hold_or_sell or hold modes allowed (no 'none')
 */
export type ConsultabilityStatus = 'consultable' | 'maintainable' | 'non_consultable';

/**
 * Request item for fetching product display data
 * investment_type is optional — backend falls back to consultability check when absent
 */
export interface ProductDisplayRequestItem {
  isin: string;
  investment_type?: string | null;
}

/**
 * Display data for a product - fetched via POST /constraints/products/display
 */
export interface ProductDisplayData {
  isin: string;
  label: string; // Product name
  currency: string; // Currency code (EUR, USD, etc.)
  consultability_status?: ConsultabilityStatus; // Determines which hold modes are available
  allowed_hold_modes: HoldMode[];
  default_hold_mode: HoldMode;
}

/**
 * Portfolio product values from platform metadata (portfolio_product_values)
 * Read-only display values for current portfolio state and optimization results
 */
export interface PortfolioProductValue {
  current_value: number; // EUR amount from imported portfolio
  current_weight: number; // 0-1 scale
  target_value: number | null; // null until rebalancing
  target_weight: number | null; // null until rebalancing
}

/**
 * Portfolio product values map from metadata
 * Key: ISIN, Value: current/target values
 */
export type PortfolioProductValuesMap = Record<string, PortfolioProductValue>;

// =============================================================================
// LOCAL STATE TYPES (for UI with 0-100 scale)
// =============================================================================

/**
 * Local state for individual/list product (0-100 scale for UI)
 */
export interface ProductItemConstraintLocal {
  weight_min: number | null;
  weight_max: number | null;
  value_min: number | null;
  value_max: number | null;
}

/**
 * Local state for portfolio product (0-100 scale for UI)
 */
export interface PortfolioProductConstraintLocal {
  weight_min: number | null;
  weight_max: number | null;
  value_min: number | null;
  value_max: number | null;
  hold_mode: HoldMode;
}

// =============================================================================
// UTILITY FUNCTIONS
// =============================================================================

/**
 * Default empty state for product constraints
 */
export const getDefaultProductConstraints = (): ProductConstraints => ({
  individual_products: {},
  list_products: {},
  selected_list_ids: [],
  portfolio_products: null,
  only_list_products: true,
});

/**
 * @deprecated Use getDefaultProductConstraints instead
 */
export const getDefaultProductConstraint = getDefaultProductConstraints;

/**
 * Summary counts for the accordion display
 */
export interface ProductSummary {
  numLists: number;
  numConstrainedProducts: number;
  totalProducts: number;
}

/**
 * Calculate summary counts from constraint data
 */
export const calculateSummary = (data: ProductConstraints | null | undefined): ProductSummary => {
  if (!data) {
    return { numLists: 0, numConstrainedProducts: 0, totalProducts: 0 };
  }

  const numLists = data.selected_list_ids?.length ?? 0;

  // Count products with constraints (any min/max is set)
  let numConstrainedProducts = 0;

  // Check individual products
  const individualProducts = data.individual_products ?? {};
  for (const product of Object.values(individualProducts)) {
    if (
      product.weight_min != null ||
      product.weight_max != null ||
      product.value_min != null ||
      product.value_max != null
    ) {
      numConstrainedProducts++;
    }
  }

  // Check list products
  const listProducts = data.list_products ?? {};
  for (const product of Object.values(listProducts)) {
    if (
      product.weight_min != null ||
      product.weight_max != null ||
      product.value_min != null ||
      product.value_max != null
    ) {
      numConstrainedProducts++;
    }
  }

  // Check portfolio products (rebalancing mode)
  const portfolioProducts = data.portfolio_products ?? {};
  for (const product of Object.values(portfolioProducts)) {
    if (
      product.weight_min != null ||
      product.weight_max != null ||
      product.value_min != null ||
      product.value_max != null ||
      product.hold_mode !== 'none'
    ) {
      numConstrainedProducts++;
    }
  }

  // Total products = individual + list + portfolio products
  const totalProducts =
    Object.keys(individualProducts).length + Object.keys(listProducts).length + Object.keys(portfolioProducts).length;

  return {
    numLists,
    numConstrainedProducts,
    totalProducts,
  };
};

/**
 * Helper to convert API scale (0-1) to UI scale (0-100)
 */
export const apiToUiScale = (value: number | null | undefined): number | null => {
  if (value == null) return null;
  return value * 100;
};

/**
 * Helper to convert UI scale (0-100) to API scale (0-1)
 */
export const uiToApiScale = (value: number | null | undefined): number | null => {
  if (value == null) return null;
  return value / 100;
};

/**
 * Check which selected lists contain a given ISIN
 * Used to determine if a product should be removed when lists are deselected
 */
export const getListsContainingProduct = (
  isin: string,
  productLists: Array<{ id: string; products: Array<{ isin: string }> }>,
  selectedListIds: string[],
): string[] => {
  return productLists
    .filter((list) => selectedListIds.includes(list.id) && list.products.some((p) => p.isin === isin))
    .map((list) => list.id);
};
